import { appContext } from '../../../../AppContext';
import { DateHelper } from '../../../../Common/DateHelper';
import { RouterProps } from 'react-router';
import { Box, Grid, Typography } from '@mui/material';
import { Announcement, UserNotification } from './DataModel';
import IbssButton from '../../../../Components/Buttons/Button/IbssButton';
import { GetAnnouncementDetails } from '../../../../Components/Cards/EventCard/GetAnnouncementDetails';
import LoadingOverlay from '../../../../Components/Navigation/LoadingOverlay/LoadingOverlay';
import IbssEventCard from '../../../../Components/Cards/EventCard/IbssEventCard';
import IbssFormControl from '../../../../Components/Forms/FormControl/IbssFormControl';
import IbssInputDropDown from '../../../../Components/Inputs/SelectList/IbssInputDropDown';
import { SelectChangeEvent } from '@mui/material';
import Helper from '../../../../Common/Helper';
import IbssCardDetailsDrawer from '../../../../Components/Cards/EventCard/IbssCardDetailsDrawer';
import { PagedResponse } from '../../../../Providers.Api/Models';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import IbssPageHeader from '../../../../Components/Headings/PageHeading/IbssPageHeader';
import IbssEventNotificationCard from '../../../../Components/Cards/EventNotificationCard/IbssEventNotificationCard';
import Notification from '../../Shared/Home/NotificationPlaceholder.png'

class MyNotifications extends IbssPage<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private get userPrefs() { return appContext().localStorageProvider.getUserPreferences()}
    private get userDetails() { return appContext().localStorageProvider.getUserDetails()}
    private announcementHelper = new GetAnnouncementDetails({});

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            buildingId: 0,
            buildingOptions: [],
            isLoading: false,
            buildingAnnouncements: [],
            buildingAnnouncementsToShow: 3,
            personalNotifications: [],
            personalNotificationsToShow: 3,
            selectedNotification: null,
        };
    }

    public async componentDidMount(): Promise<void>
    {       
        this.pageTitle = this.labels.HubMenuNotifications;
        this.loadBuildings();
        const defaultBuildingId = this.userPrefs.SearchPrefs.DefaultBuilding ?? this.state.buildingOptions[0].value;
        this.setState({buildingId: defaultBuildingId});
        
        await this.getUserNotifications(1); // nodeId fixed at 1 as user need to get their notifications across all buildings.
        await this.getNotifications(defaultBuildingId);
    }

    private loadBuildings(): void
    {
        const buildings = Helper.getAllBuildingsData().sort((a, b) => a.Name.localeCompare(b.Name));
        const options = buildings.map(i => ({ label: i.Name, value: i.Node_Id }));
        this.setState({ buildingOptions: options });
    }

    private get personalNotifications(): UserNotificationView[]
    {
        return this.state.personalNotifications.slice(0, this.state.personalNotificationsToShow);
    }
    private get buildingAnnouncements(): AnnouncementView[]
    {
        return this.state.buildingAnnouncements.slice(0, this.state.buildingAnnouncementsToShow);
    }

    private async getUserNotifications(nodeId: number): Promise<void>
    {
        let skipToken = '';
        let notifications: UserNotificationView[] = [];
        
        try
        {
            const userEmail = this.userDetails.email;
            this.setState({isLoading: true});
            const buildingNow = DateHelper.now().toUtcByNode(this.state.buildingId);
            const queryFilter = `Notification_User_Email eq '${userEmail}' and Classification eq 'YourAlerts' and Notification_Status eq 'Active' and Expiry_Date gt datetime'${buildingNow}' and Available_From le datetime'${buildingNow}'`;
            do
            {
                const response = await appContext().ibssApiClientV2.v2.byNodeid.userNotifications.get<PagedResponse<UserNotification[]>>
                ({
                    nodeId: nodeId,
                    select: UserNotification,
                    filter: queryFilter,
                    recursive: nodeId === 1,
                    orderBy: 'Available_From desc',
                    skipToken: skipToken,
                    top: 100,
                });
    
                const responseNotifications = response.value.map(notification => UserNotificationView.fromNotificationToView(notification));
                skipToken = response.skipToken;
                notifications = [...notifications, ...responseNotifications];
            }
            while(skipToken !== '');
            
            const sortedNotifications = notifications
            .filter(notification => (notification.isEventNotification && notification.eventNotificationMessage) || !notification.isEventNotification)
            .toSorted((a,b) => b.availableFrom.toMillis() - a.availableFrom.toMillis());
            this.setState({personalNotifications: sortedNotifications});
            
        }
        finally
        {
            this.setState({isLoading: false});
        } 
    }

    private async getNotifications(nodeId: number): Promise<void>
    {
        let skipToken = '';
        let notifications: AnnouncementView[] = [];
        
        try
        {
            this.setState({isLoading: true});
            const queryFilter = `Classification eq 'Building' and Notification_Target eq 1`;
            
            do
            {
                const response = await appContext().ibssApiClientV2.v2.byNodeid.announcements.active.get<PagedResponse<Announcement[]>>
                ({
                    nodeId: nodeId,
                    select: Announcement,
                    filter: queryFilter,
                    recursive: nodeId === 1,
                    orderBy: 'Available_From desc',
                    skipToken: skipToken,
                    top: 100,
                });
    
                const responseNotifications = response.value.map(notification => AnnouncementView.fromAnnouncementToView(notification));
                skipToken = response.skipToken;


                notifications = [...notifications, ...responseNotifications];
            }
            while(skipToken !== '');
            
            const sortedNotifications = notifications.toSorted((a,b) => b.availableFrom.toMillis() - a.availableFrom.toMillis());

            this.setState({buildingAnnouncements: sortedNotifications});
            
        }
        finally
        {
            this.setState({isLoading: false});
        }
    }

    private getNotificationTitle(title: string, categoryLabel: string): string
    {
        return `${categoryLabel} - ${title}`;
    }

    private navigateToDetail(nodeId: number, notificationId: string): void
    {
        const notifications = [...this.state.buildingAnnouncements, ...this.state.personalNotifications];
        const selected = notifications.find(({id, nodeId: nId}) => nId === nodeId && id === notificationId);
        if(!selected)
        {
            return;
        }

        this.setState({ selectedNotification: selected });
    }

    private navigateToEvent(nodeId: number, notificationId: string): void
    {
        const notifications = [...this.state.personalNotifications];
        const selected = notifications.find(({id, nodeId: nId}) => nId === nodeId && id === notificationId);
        if(!selected)
        {
            return;
        }
        const { eventId } = selected;
        this.props.history.push(`/flex/events/${eventId}/${nodeId}`);
    } 

    private closeNotificationsDetails(): void
    {
        this.setState({ selectedNotification: null });
    }

    private async handleBuildingChange(event: SelectChangeEvent<number>): Promise<void>
    {
        const newBuildingId = Number(event.target.value);
        await this.setStateAsync
        ({
            buildingId: newBuildingId,
            buildingAnnouncements: [],
        });

        await this.getNotifications(newBuildingId);
    }

    private showMoreNotificationsClicked(classification: NotificationClass, extraNotifications: number): void
    {

        if(classification === NotificationClass.Personal)
        {
            this.setState({personalNotificationsToShow: this.state.personalNotificationsToShow + extraNotifications});
        }
        else
        {
            this.setState({buildingAnnouncementsToShow: this.state.buildingAnnouncementsToShow + extraNotifications});
        }
    }

    private showGetMoreNotifications(classification: NotificationClass): boolean
    {
        // if the number of notifications on display are less than the total notifications of the type, make see previous notifications button visible.
        const notificationsOnDisplay = classification === NotificationClass.Personal ? this.personalNotifications: this.buildingAnnouncements;
        const totalNotifications = classification === NotificationClass.Personal ? this.state.personalNotifications: this.state.buildingAnnouncements;
        return notificationsOnDisplay.length < totalNotifications.length;
    }

    public render(): JSX.Element
    {
        const showMoreBuildingAnnouncements = this.showGetMoreNotifications(NotificationClass.Building);
        const showMorePersonalNotifications = this.showGetMoreNotifications(NotificationClass.Personal);

        return (
                <div className="page-height-exct-header">
                    <div className="rightPanel-main-content">
                    <Box display={"flex"} pb={5} justifyContent={"space-between"} alignItems={"center"}>
                        {
                            this.state.isLoading && <LoadingOverlay/>
                        }
                    </Box>
                    {
                        !this.state.isLoading && <Box paddingBottom={'1em'}>
                            
                            {/* ------- Your Notifications sections starts here ------- */}
                            <Box pb={2}>
                                <Typography className="font-ssp" variant='h6' mb={2}>{this.labels.HubLabelYourNotifications}</Typography>
                                {
                                    this.state.personalNotifications.length === 0 && <Typography variant='body1'>{this.labels.funcNoNotifications_D}</Typography>
                                }
                                <Grid container spacing={2} mb="1em">
                                {
                                        this.personalNotifications.map(announcement => 
                                            <Grid 
                                                item 
                                                sm={12} 
                                                key={announcement.id}
                                            >

                                                {announcement.isEventNotification &&
                                                    <IbssEventNotificationCard
                                                        id={announcement.id}
                                                        title={announcement.eventTitle}
                                                        message={announcement.eventNotificationMessage}
                                                        actionURL={announcement.actionURI}
                                                        imageURI={announcement.imageURI}
                                                        nodeId={announcement.nodeId}
                                                        showDetails={true}
                                                        navigateToDetails={()=> this.navigateToEvent(announcement.nodeId, announcement.id)}
                                                    />
                                                }

                                                {!announcement.isEventNotification &&
                                                    <IbssEventCard
                                                        id={announcement.id}
                                                        title={this.getNotificationTitle(announcement.title, announcement.subclassification)}
                                                        description={announcement.shortMessage}
                                                        actionURL={announcement.actionURI}
                                                        imageURI={announcement.imageURI}
                                                        fullDescription={announcement.message}
                                                        itemDate={announcement.availableFrom}
                                                        startTime={announcement.availableFrom}
                                                        nodeId={announcement.nodeId}
                                                        status={this.announcementHelper.getNotificationStatus(announcement.priority)}
                                                        showDetails={true}
                                                        navigateToDetails={()=> this.navigateToDetail(announcement.nodeId, announcement.id)}
                                                    />
                                                }
                                            </Grid>
                                        )
                                }
                                </Grid>
                                {
                                    showMorePersonalNotifications && 
                                    <IbssButton
                                        variant='contained'
                                        fullWidth
                                        color='secondary'
                                        onClick={()=> this.showMoreNotificationsClicked(NotificationClass.Personal, 3)}
                                    >
                                        <Typography className="font-ssp">
                                            {this.labels.funcPreviousAnouncements_L}
                                        </Typography>
                                    </IbssButton>
                                }
                            </Box>
                            {/* ------- Your Notifications sections ends here -------*/}

                            {/* ------- Building Announcements sections starts here ------- */}
                            <Box pb={2}>
                                <Box display={"flex"} pb={3} justifyContent={"space-between"} alignItems={"center"}>
                                    <Typography className="font-ssp" variant='h6' mb={2}>{this.labels.funcBuildingAnnouncement_S}</Typography>
                                    {
                                        this.state.buildingAnnouncements.length === 0 && <Typography variant='body1'>{this.labels.funcNoNotifications_D}</Typography>
                                    }
                                    
                                    <IbssFormControl>
                                        <IbssInputDropDown
                                            id="buildingSelection"
                                            inputLabel={this.labels.HubLabelBuilding}
                                            minWidth={200}
                                            options={this.state.buildingOptions}
                                            value={this.state.buildingId}
                                            onChange={(e) => this.handleBuildingChange(e)}
                                        />
                                    </IbssFormControl>
                                </Box>
                                <Grid container spacing={2} mb="1em">
                                {
                                        this.buildingAnnouncements.map(announcement => 
                                            <Grid 
                                                item 
                                                sm={12} 
                                                key={announcement.id}
                                            >
                                                <IbssEventCard
                                                    id={announcement.id}
                                                    title={this.getNotificationTitle(announcement.title, announcement.subclassification)}
                                                    description={announcement.shortMessage}
                                                    actionURL={announcement.actionURI}
                                                    imageURI={announcement.imageURI && announcement.imageURI.trim() !== '' ? announcement.imageURI : Notification}
                                                    fullDescription={announcement.message}
                                                    itemDate={announcement.availableFrom}
                                                    startTime={announcement.availableFrom}
                                                    nodeId={announcement.nodeId}
                                                    status={this.announcementHelper.getNotificationStatus(announcement.priority)}
                                                    showDetails={true}
                                                    navigateToDetails={()=> this.navigateToDetail(announcement.nodeId, announcement.id)}
                                                />
                                            </Grid>
                                        )
                                }
                                </Grid>
                                {
                                    showMoreBuildingAnnouncements && 
                                    <IbssButton
                                        variant='contained'
                                        fullWidth
                                        color='secondary'
                                        onClick={()=> this.showMoreNotificationsClicked(NotificationClass.Building, 3)}
                                    >
                                        <Typography className="font-ssp">
                                            {this.labels.funcPreviousAnouncements_L}
                                        </Typography>
                                    </IbssButton>
                                }
                            </Box>
                            {/* ------- Building Announcements sections ends here ------- */}
                        </Box>
                    }
                    {/* -------Notification Details Drawer starts here ------- */}
                    {this.state.selectedNotification &&
                        <IbssCardDetailsDrawer
                            open={!!this.state.selectedNotification}
                            onClose={()=> this.closeNotificationsDetails()}
                            nodeId={this.state.selectedNotification.nodeId}
                            availableFrom = {this.state.selectedNotification.availableFrom}
                            imageURI={this.state.selectedNotification.imageURI.trim() !== '' ? this.state.selectedNotification.imageURI : Notification}
                            priority={this.state.selectedNotification.priority}
                            title={this.state.selectedNotification.title}
                            shortMessage={this.state.selectedNotification.shortMessage}
                            message={this.state.selectedNotification.message}
                        />
                    }
                    {/* -------Notification Details Drawer ends here ------- */}
                    </div>
                </div>
        )
    }
}

export default MyNotifications;

export interface IProps extends RouterProps
{
}

export interface IState
{
    buildingId: number,
    buildingOptions: IListOption<number>[],
    isLoading: boolean,
    buildingAnnouncements: AnnouncementView[],
    buildingAnnouncementsToShow: number,
    personalNotifications: UserNotificationView[],
    personalNotificationsToShow: number,
    selectedNotification: AnnouncementView | UserNotificationView | null;
}

interface IListOption<TValue>
{
    label: string,
    value: TValue,
}

enum NotificationTarget
{
    None = 0,
    RoamerAndFlex = 1,
    Inspire = 2,
    Unity = 4,
    Guest = 8,
}
class AnnouncementView
{
    public id = '';
    public title = '';
    public priority = 0;
    public recordId = '';
    public message = '';
    public shortMessage = '';
    public reminders =  "";
    public type = 0;
    public status = '';
    public userEmail = '';
    public sent = 0;
    public target = NotificationTarget.None;
    public device = null;
    public classification = '';
    public subclassification = '';
    public availableFrom = DateHelper.null();
    public expiryDate = DateHelper.null();
    public imageURI = '';
    public actionURI = '';
    public nodeId = 0;


    public static fromAnnouncementToView(announcement: Announcement): AnnouncementView
    {
        return {
            id: `${announcement.Notification_Id}`,
            nodeId: announcement.Node_Id,
            title: announcement.Notification_Title,
            priority: announcement.Notification_Priority,
            recordId: announcement.Notification_Record_Id,
            message: announcement.Notification_Message,
            shortMessage: announcement.Notification_Short_Message,
            reminders: announcement.Notification_Reminders,
            type: announcement.Notification_Type,
            status: announcement.Notification_Status,
            userEmail: announcement.Notification_User_Email,
            sent: announcement.Notification_Sent,
            target: this.getTargetName(announcement.Notification_Target),
            device: announcement.Notification_Device,
            classification: announcement.Classification,
            subclassification: announcement.Subclassification,
            availableFrom: announcement.Available_From,
            expiryDate: announcement.Expiry_Date,
            imageURI: announcement.ImageURI,
            actionURI: announcement.ActionURI,
        }
    }

    public static getTargetName(target: number | null): NotificationTarget
    {
        switch(target)
        {
            case 0:
                return NotificationTarget.None;
            case 1:
                return NotificationTarget.RoamerAndFlex;
            case 2:
                return NotificationTarget.Inspire;
            case 4:
                return NotificationTarget.Unity;
            case 8:
                return NotificationTarget.Guest
            default:
                return NotificationTarget.None;
        }
    }
}

enum NotificationClass
{
    Personal = 'Personal',
    Building = 'Building,'
}

interface INotificationData
{
    Notification_Message: string | null; // some records have these properties set to null
    Notification_Short_Message: string | null;
}

interface IEventNotificationData
{
    Event_Description: string;
    Event_Short_Description: string;
    Ticketed_Event_Id: string;
    Event_Status: string;
    Event_Title: string;
    Participant?: IEventParticipant;
}

interface IEventParticipant
{
    Booking_Participant_Email: string;
    Booking_Participant_Name: string;
    Booking_Participant_Organisation: string;
    Booking_Participant_Type: number;
    IsParticipant_On_WaitingList: number;
}

class INotificationDataView
{
    public isEventNotification = false;
    public message = '';
    public shortMessage = '';
    public eventId = '';
    public eventStatus = '';
    public eventTitle = '';
    public bookingParticipantEmail =  '';
    public bookingParticipantName = '';
    public bookingParticipantOrganisation = '';
    public bookingParticipantType = 0;
    public isParticipantOnWaitingList = 0;

    public static fromNotificationDataToView(data: INotificationData | IEventNotificationData | null): INotificationDataView
    {
        if(data === null)
        {
            return {
                isEventNotification: false,
                message: '',
                shortMessage: '',
                eventId: '',
                eventStatus: '',
                eventTitle: '',
                bookingParticipantEmail: '',
                bookingParticipantName: '',
                bookingParticipantOrganisation: '',
                bookingParticipantType: 0,
                isParticipantOnWaitingList: 0,
            };
        }

        const isEventNotification = 'Event_Status' in data;
        if(isEventNotification)
        {
            return {
                isEventNotification: isEventNotification,
                message: data.Event_Description,
                shortMessage: data.Event_Short_Description,
                eventId: data.Ticketed_Event_Id,
                eventStatus: data.Event_Status,
                eventTitle: data.Event_Title,
                bookingParticipantEmail: data.Participant?.Booking_Participant_Email ?? '',
                bookingParticipantName: data.Participant?.Booking_Participant_Name ?? '',
                bookingParticipantOrganisation: data.Participant?.Booking_Participant_Email ?? '',
                bookingParticipantType: data.Participant?.Booking_Participant_Type ?? 0,
                isParticipantOnWaitingList: data.Participant?.IsParticipant_On_WaitingList ?? 0,
            };
        }
        else
        {
            return {
                isEventNotification: isEventNotification,
                message: data.Notification_Message || '',
                shortMessage: data.Notification_Short_Message || '',
                eventId: '',
                eventStatus: '',
                eventTitle: '',
                bookingParticipantEmail: '',
                bookingParticipantName: '',
                bookingParticipantOrganisation: '',
                bookingParticipantType: 0,
                isParticipantOnWaitingList: 0,
            };
        }
    }
}
class UserNotificationView
{
    public id = '';
    public title = '';
    public priority = 0;
    public message = '';
    public shortMessage = '';
    public type = 0;
    public status = '';
    public userEmail = '';
    public sent = 0;
    public target = NotificationTarget.None;
    public classification = '';
    public subclassification = '';
    public availableFrom = DateHelper.null();
    public expiryDate = DateHelper.null();
    public imageURI = '';
    public actionURI = '';
    public nodeId = 0;
    public category = '';
    public isEventNotification = false;
    public eventId = '';
    public eventStatus = '';
    public eventTitle = '';
    public bookingParticipantEmail =  '';
    public bookingParticipantName = '';
    public bookingParticipantOrganisation = '';
    public bookingParticipantType = 0;
    public isParticipantOnWaitingList = 0;
    public eventNotificationMessage = '';


    public static fromNotificationToView(notification: UserNotification): UserNotificationView
    {
        
        const notificationData = JSON.parse(notification.Notification_Data) as unknown as INotificationData | IEventNotificationData | null;
        const notificationDataView = INotificationDataView.fromNotificationDataToView(notificationData);
        const { isEventNotification, eventId, eventStatus, eventTitle, bookingParticipantEmail, bookingParticipantName, bookingParticipantOrganisation, bookingParticipantType, isParticipantOnWaitingList } = notificationDataView;

        return {
            id: `${notification.UserNotification_Id}`,
            nodeId: notification.Node_Id,
            title: notification.Notification_Title,
            priority: notification.Notification_Priority,
            message: notificationDataView?.message ?? '',
            shortMessage: notificationDataView?.shortMessage ?? '',
            type: notification.Notification_Type,
            status: notification.Notification_Status,
            userEmail: notification.Notification_User_Email,
            sent: notification.Notification_Sent,
            target: this.getTargetName(notification.Notification_Target),
            classification: notification.Classification,
            subclassification: notification.Subclassification,
            availableFrom: notification?.Available_From,
            expiryDate: notification?.Expiry_Date,
            imageURI: notification.ImageURI,
            actionURI: notification.ActionURI,
            category: notification.Notification_Category,
            isEventNotification: isEventNotification,
            eventId: eventId,
            eventStatus: eventStatus,
            eventTitle: eventTitle,
            bookingParticipantEmail: bookingParticipantEmail,
            bookingParticipantName: bookingParticipantName,
            bookingParticipantOrganisation: bookingParticipantOrganisation,
            bookingParticipantType: bookingParticipantType,
            isParticipantOnWaitingList: isParticipantOnWaitingList,
            eventNotificationMessage: this.getEventNotificationMessage(
                isEventNotification, 
                notification.Notification_Category, 
                eventStatus,
                bookingParticipantType,
                isParticipantOnWaitingList,
            ),
            
        }
    }

    public static getTargetName(target: number | null): NotificationTarget
    {
        switch(target)
        {
            case 0:
                return NotificationTarget.None;
            case 1:
                return NotificationTarget.RoamerAndFlex;
            case 2:
                return NotificationTarget.Inspire;
            case 4:
                return NotificationTarget.Unity;
            case 8:
                return NotificationTarget.Guest
            default:
                return NotificationTarget.None;
        }
    }

    public static getEventNotificationMessage(
        isEventNotification: boolean, 
        notificationCategory: string, 
        eventStatus: string,
        bookingParticipantType: number,
        isParticipantOnWaitingList: number,
    ): string
    {
        const labels =  appContext().labels;
        if(!isEventNotification)
        {
            return '';
        }
        else if (notificationCategory === 'TicketedEventReminder')
        {
            return labels.funcEventNotificationReminder;
        }
        else if (notificationCategory === 'TicketedEventChange')
        {
            return labels.funcEventNotificationUpdate
        }
        else if(notificationCategory === 'TicketedEventStatus' && eventStatus === 'Cancelled')
        {
            return labels.funcEventNotificationCancelled;
        }
        else if(notificationCategory === 'TicketedEventParticipant' && bookingParticipantType === 1 && isParticipantOnWaitingList === 0)
        {
            return labels.funcEventNotificationSignedUp;
        }
        else if(notificationCategory === 'TicketedEventParticipant' && bookingParticipantType === 1 && isParticipantOnWaitingList === 1)
        {
            return labels.funcEventNotificationWaitlisted;
        }
        else
        {
            return '';
        }
    }
}